<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack
      :fallback="booking ? { name: 'bookingEdit', params: { id: booking?.id } } : undefined"
      >{{ t('back') }}</ButtonBack
    >
  </ButtonBackContainer>

  <section class="carvia-container flex flex-col justify-center">
    <div v-if="!isLoading" class="grow space-y-6">
      <div v-if="carOptions.length > 1" class="grid grid-cols-3 gap-4">
        <Select
          v-model="selectedCarId"
          variant="ghost"
          :label="t('damagesOfCar')"
          :options="carOptions"
        />
      </div>
      <Damages
        v-if="booking && selectedCarId"
        :key="selectedCarId"
        :car-id="selectedCarId"
        :booking-id="booking.id"
        :add-damage-button-text="t('addDamageToBooking')"
      />
    </div>
    <div v-else class="flex justify-center">
      <Spinner />
    </div>
  </section>
</template>

<script setup lang="ts">
import Damages from '@/components/booking-forms/damages/Damages.vue';
import { useRoute } from 'vue-router';
import { useBooking } from '@/queries/use-bookings';
import { useI18n } from 'vue-i18n';
import { useUiStore } from '@/stores/ui.store';
import { computed, nextTick, ref, watch, watchEffect } from 'vue';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import Select from '@/components/Select.vue';
import { uniqBy } from 'lodash';

const { t } = useI18n();
const route = useRoute();
const uiStore = useUiStore();
const { data: booking, isLoading } = useBooking(route.params.id as string);

const selectedCarId = ref<string | null>(null);

const unwatchBooking = watch(
  booking,
  () => {
    if (booking.value) {
      selectedCarId.value = booking.value.allocations[0].car.id;
      nextTick(() => {
        unwatchBooking();
      });
    }
  },
  { immediate: true },
);

watchEffect(() => {
  if (booking.value) {
    const { customerData, bookedCar } = booking.value;
    uiStore.setHeaderTitle(
      t('booking'),
      `${customerData?.firstName ?? 'n/a'} ${customerData?.lastName ?? 'n/a'}`,
      `${bookedCar.vehicleType?.make.name} ${bookedCar.vehicleType?.model}`,
    );
  } else {
    uiStore.setHeaderTitle(t('booking'));
  }
});

const carOptions = computed(() => {
  const uniqueAllocations = uniqBy(booking.value?.allocations, 'car.id');
  return uniqueAllocations.map((allocation) => {
    return {
      label: `${allocation.car.vehicleType.make.name} ${allocation.car.vehicleType.model} (${allocation.car.licencePlate})`,
      value: allocation.car.id,
    };
  });
});
</script>
<i18n lang="json">
{
  "en": {
    "back": "back to booking edit",
    "booking": "Booking",
    "addDamageToBooking": "+ Add damage to booking",
    "damagesOfCar": "Damages of car"
  },
  "de": {
    "back": "Zurück zur Buchungsbearbeitung",
    "booking": "Buchung",
    "addDamageToBooking": "+ Schaden zur Buchung hinzufügen",
    "damagesOfCar": "Schäden des Autos"
  },
  "es": {
    "back": "volver a editar reserva",
    "booking": "Reserva",
    "addDamageToBooking": "+ Añadir daño a la reserva",
    "damagesOfCar": "Daños del vehículo"
  }
}
</i18n>
